import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { Download, FileText, Calendar, Database } from 'lucide-react';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import swal from 'sweetalert';
import { toast } from 'sonner';

const SieExportModal = ({ show, onHide, fiscalYears, companyId }) => {
    const [selectedFiscalYear, setSelectedFiscalYear] = useState('');
    const [isExporting, setIsExporting] = useState(false);

    const handleExport = async () => {
        try {
            setIsExporting(true);

            const url = getApiUrl(`${companyId}/accounting/sie/export${selectedFiscalYear ? `?fiscalYearId=${selectedFiscalYear}` : ''}`);

            const response = await axios.get(url, {
                withCredentials: true,
                responseType: 'blob'
            });

            // Create blob and download
            const blob = new Blob([response.data], { type: 'application/octet-stream' });
            const downloadUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = downloadUrl;

            // Generate filename
            const selectedYear = fiscalYears.find(year => year.fiscal_year_id === selectedFiscalYear);
            const yearSuffix = selectedYear ? `-${selectedYear.year}` : '';
            const dateSuffix = new Date().toISOString().slice(0, 10);
            link.download = `export${yearSuffix}-${dateSuffix}.se`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(downloadUrl);

            toast.success("SIE-fil exporterad framgångsrikt!");
            onHide();
        } catch (error) {
            console.error('Export error:', error);
            toast.error("Misslyckades med att exportera SIE-fil");
        } finally {
            setIsExporting(false);
        }
    };

    const resetModal = () => {
        setSelectedFiscalYear('');
        setIsExporting(false);
    };

    const handleClose = () => {
        resetModal();
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} size="md" centered>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center">
                    <FileText className="me-2" size={24} />
                    Exportera SIE-fil
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center">
                        <Calendar className="me-2" size={16} />
                        Välj räkenskapsår
                    </Form.Label>
                    <Form.Select
                        value={selectedFiscalYear}
                        onChange={(e) => setSelectedFiscalYear(e.target.value)}
                        disabled={isExporting}
                        data-testid="sie-export-fiscal-year-select"
                    >
                        {/*<option value="">Alla räkenskapsår</option>*/}
                        {fiscalYears.map((year) => (
                            <option key={year.fiscal_year_id} value={year.fiscal_year_id}>
                                {year.year} ({new Date(year.start_date).getFullYear()})
                            </option>
                        ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                        Lämna tomt för att exportera alla räkenskapsår
                    </Form.Text>
                </Form.Group>

                {selectedFiscalYear && (
                    <Alert variant="success">
                        Exporterar räkenskapsår: <strong>
                            {fiscalYears.find(y => y.fiscal_year_id === selectedFiscalYear)?.year}
                        </strong>
                    </Alert>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={isExporting}>
                    Avbryt
                </Button>
                <Button variant="primary" onClick={handleExport} disabled={isExporting}>
                    {isExporting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Exporterar...
                        </>
                    ) : (
                        <>
                            <Download className="me-2" size={16} />
                            Exportera SIE-fil
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SieExportModal;